import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { Toaster } from 'react-hot-toast';
import { Capacitor } from "@capacitor/core";
import { App as CapApp } from "@capacitor/app";
import { Keyboard } from "@capacitor/keyboard";
import { appLinkRoute, launchUrlNotYetHandled } from "./features/payments/appLinks";
import { backAction } from "./utils/nativeBack";

import Navbar from "./components/common/Navbar";
import OfflineBanner from "./components/common/OfflineBanner";
import Home from "./pages/Home";
import BookingPage from "./pages/BookingPage";
import Products from "./features/shop/Products";
import CreateUser from "./features/auth/components/CreateUser";
import LogIn from "./features/auth/components/LogIn";
import ForgotPassword from "./features/auth/components/ForgotPassword";
import AuthAction from "./features/auth/components/AuthAction";
import Profile from "./features/auth/components/Profile";
import MyCart from "./features/shop/MyCart";
import PaymentReturn from "./features/shop/PaymentReturn";
import Settings from "./features/auth/SettingsTabs/Settings";
import Tickets from "./features/tickets/Tickets";
import Support from "./features/auth/SettingsTabs/Support";
import Orders from "./features/auth/SettingsTabs/Orders";
import Coupons from "./features/auth/SettingsTabs/Coupons";
import Privacy from "./features/auth/SettingsTabs/Privacy";

import KlippekortRedeem from "./features/tickets/KlippekortRedeem";

import PageTransition from "./components/common/PageTransition";
import PageHeader from "./components/common/pageHeader";
import { BackHandlerProvider } from "./components/common/BackHandlerContext";
import AuthRedirect from "./components/common/AuthRedirect";
import ErrorBoundary from "./components/common/ErrorBoundary";
import NotFound from "./components/common/NotFound";
import SessionExpiredGate from "./components/common/SessionExpiredGate";
import "./styles/global.css";

function AnimatedRoutes() {
  const navigate = useNavigate();
  
  useEffect(() => {
    const setAppHeight = () => {
      document.documentElement.style.setProperty('--app-height', `${window.innerHeight}px`);
    };
    setAppHeight();
    window.addEventListener('resize', setAppHeight);
    
    // Check for redirect path from 404.html
    const redirectPath = sessionStorage.getItem('redirectPath');
    if (redirectPath && redirectPath !== '/') {
      sessionStorage.removeItem('redirectPath');
      navigate(redirectPath, { replace: true });
    }
    
    return () => window.removeEventListener('resize', setAppHeight);
  }, [navigate]);

  // Android/iOS app: after Vipps the phone opens the app with the payment
  // return link. getLaunchUrl covers Android having closed the app while the
  // customer was in Vipps (cold start), appUrlOpen the app still running.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;
    const open = (url) => {
      const route = appLinkRoute(url);
      if (route) navigate(route, { replace: true });
    };
    // navigate changes on every page change (BrowserRouter), so this effect
    // re-runs - the launch link must only be followed once, also across reloads.
    if (launchUrlNotYetHandled()) {
      CapApp.getLaunchUrl().then((launch) => launch && open(launch.url));
    }
    const listener = CapApp.addListener('appUrlOpen', ({ url }) => open(url));
    return () => { listener.then((l) => l.remove()); };
  }, [navigate]);

  // App keyboard: the web view shrinks to the space above it (capacitor.config
  // "resize": "native" - before, iOS pushed the whole page and its header up
  // under the status bar); the bottom menu hides meanwhile so forms get the room.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;
    const show = Keyboard.addListener('keyboardWillShow', () => document.body.classList.add('keyboard-open'));
    const hide = Keyboard.addListener('keyboardWillHide', () => document.body.classList.remove('keyboard-open'));
    return () => { show.then((l) => l.remove()); hide.then((l) => l.remove()); };
  }, []);

  // Phone's back button in the app: see backAction (utils/nativeBack.js).
  // An open sheet marks its history step (useCloseOnBack), so going back one
  // step closes it.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;
    const listener = CapApp.addListener('backButton', () => {
      // React Router numbers its history steps (idx); 0 = the first page this app run.
      const canGoBack = (window.history.state?.idx ?? 0) > 0;
      const action = backAction(window.location.pathname, Boolean(window.history.state?.overlay), canGoBack);
      if (action === 'exit') CapApp.exitApp();
      else if (action === 'home') navigate('/', { replace: true });
      else window.history.back();
    });
    return () => { listener.then((l) => l.remove()); };
  }, [navigate]);

  const location = useLocation();

  // Header's refresh button (pageHeader.jsx): remount the open page so it
  // fetches its data again, fading it in rather than reloading the whole app.
  const [refresh, setRefresh] = useState({ key: 0, fading: false });
  useEffect(() => {
    const onRefresh = () => {
      setRefresh((r) => ({ key: r.key + 1, fading: true }));
      setTimeout(() => setRefresh((r) => ({ ...r, fading: false })), 300);
    };
    window.addEventListener('pws:refresh', onRefresh);
    return () => window.removeEventListener('pws:refresh', onRefresh);
  }, []);

  const routesContent = (
    <AnimatePresence mode="sync">
      <Routes location={location} key={location.pathname}>
        <Route
          path="/"
          element={
            <PageTransition>
              <div className="container-600">
                <Home />
              </div>
            </PageTransition>
          }
        />
        <Route
          path="/paymentreturn"
          element={
            <PageTransition>
              <div className="container-600">
                <PaymentReturn />
              </div>
            </PageTransition>
          }
        />
        <Route
          path="/about"
          element={
            <PageTransition>
              <div className="container-600">
                <NotFound />
              </div>
            </PageTransition>
          }
        />

        <Route
          path="/BookingPage"
          element={
            <PageTransition>
              <div className="container-600">
                <BookingPage />
              </div>
            </PageTransition>
          }
        />
        <Route
          path="/Products"
          element={
            <PageTransition>
              <div className="container-600">
                <Products />
              </div>
            </PageTransition>
          }
        />
        <Route
          path="/CreateUser"
          element={
            <PageTransition>
              <CreateUser />
            </PageTransition>
          }
        />
        <Route
          path="/LogIn"
          element={
            <AuthRedirect>
              <PageTransition>
                <div className="container-600">
                  <LogIn />
                </div>
              </PageTransition>
            </AuthRedirect>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PageTransition>
              <div className="container-600">
                <ForgotPassword />
              </div>
            </PageTransition>
          }
        />
        <Route
          path="/auth-action"
          element={
            <PageTransition>
              <div className="container-600">
                <AuthAction />
              </div>
            </PageTransition>
          }
        />
        <Route
          path="/MyCart"
          element={
            <PageTransition>
              <div className="container-600">
                <MyCart />
              </div>
            </PageTransition>
          }
        />
        <Route
          path="/Profile"
          element={
            <PageTransition>
              <div className="container-600">
                <Profile />
              </div>
            </PageTransition>
          }
        />
        <Route
          path="/Settings"
          element={
            <PageTransition>
              <div className="container-600">
                <Settings />
              </div>
            </PageTransition>
          }
        />
        <Route
          path="/tickets"
          element={
            <PageTransition>
              <div className="container-600">
                <Tickets />
              </div>
            </PageTransition>
          }
        />
        <Route
          path="/Orders"
          element={
            <PageTransition>
              <div className="container-600">
                <Orders />
              </div>
            </PageTransition>
          }
        />
        <Route
          path="/support"
          element={
            <PageTransition>
              <div className="container-600">
                <Support />
              </div>
            </PageTransition>
          }
        />
        <Route
          path="/personvern"
          element={
            <PageTransition>
              <div className="container-600">
                <Privacy />
              </div>
            </PageTransition>
          }
        />
        <Route
          path="/coupons"
          element={
            <PageTransition>
              <div className="container-600">
                <Coupons />
              </div>
            </PageTransition>
          }
        />
        <Route
          path="/klippekort-redeem"
          element={
            <PageTransition>
              <div className="container-600">
                <KlippekortRedeem />
              </div>
            </PageTransition>
          }
        />
        <Route
          path="*"
          element={
            <PageTransition>
              <div className="container-600">
                <NotFound />
              </div>
            </PageTransition>
          }
        />
      </Routes>
    </AnimatePresence>
  );

  return (
    <>
      <PageHeader />
      <ErrorBoundary key={`${location.pathname}#${refresh.key}`}>
        <div className={refresh.fading ? 'page-refresh-fade' : undefined}>
          {routesContent}
        </div>
      </ErrorBoundary>
    </>
  );
}

// iOS Safari ignores the viewport meta tag's user-scalable=no, so pinch-zoom and
// double-tap-zoom still work there unless blocked at the gesture-event level.
function usePinchZoomDisabled() {
  useEffect(() => {
    const preventGesture = (e) => e.preventDefault();
    const preventPinch = (e) => { if (e.touches.length > 1) e.preventDefault(); };
    document.addEventListener('gesturestart', preventGesture);
    document.addEventListener('gesturechange', preventGesture);
    document.addEventListener('touchmove', preventPinch, { passive: false });
    return () => {
      document.removeEventListener('gesturestart', preventGesture);
      document.removeEventListener('gesturechange', preventGesture);
      document.removeEventListener('touchmove', preventPinch);
    };
  }, []);
}

function App() {
  usePinchZoomDisabled();
  return (
    <Router>
      <BackHandlerProvider>
        <OfflineBanner />
        <Navbar />
        <SessionExpiredGate />
        <AnimatedRoutes />
        <Toaster
          position="top-center"
          // iPhone: keep toasts below the Dynamic Island/notch (0 on web, same 16px as before).
          containerStyle={{ top: 'calc(env(safe-area-inset-top) + 16px)' }}
          toastOptions={{
            duration: 6000,
            style: {
              background: '#363636',
              color: '#fff',
            },
          }}
        />
      </BackHandlerProvider>
    </Router>
  );
}

export default App;
